// pages/admin/Export.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Download as DownloadIcon
} from '@mui/icons-material';
import AppLayout from '../../components/AppLayout';
import API_BASE_URL from '../../config/api';

const Export = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleExport = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Validate custom date range
      if (dateRange === 'custom' && (!startDate || !endDate)) {
        setError('Please select both start and end dates');
        setLoading(false);
        return;
      }

      // Build query params for date filtering
      const params = new URLSearchParams();
      params.append('dateRange', dateRange);
      if (dateRange === 'custom') {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }

      // Fetch data from API
      const token = localStorage.getItem('token');
      
      const [customersRes, staffRes, suppliersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/export/customers?${params}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/export/staff?${params}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/export/suppliers?${params}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (!customersRes.ok || !staffRes.ok || !suppliersRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [customersData, staffData, suppliersData] = await Promise.all([
        customersRes.json(),
        staffRes.json(),
        suppliersRes.json()
      ]);

      // Ensure all data is in array format
      const customers = Array.isArray(customersData) ? customersData : [];
      const staff = Array.isArray(staffData) ? staffData : [];
      const suppliers = Array.isArray(suppliersData) ? suppliersData : [];

      // Generate PDF using the existing working pdfGenerator
      const { generateBusinessReportPDF } = await import('../../utils/pdfGenerator');
      generateBusinessReportPDF({
        customers,
        staff,
        suppliers,
        dateRange: {
          type: dateRange,
          startDate,
          endDate
        }
      });
      
      setSuccess('Business report exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setError(error.message || 'Failed to export business report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        {/* Header */}
        <Paper elevation={2} sx={{ p: 4, mb: 3, textAlign: 'center', background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', color: '#bc985e' }}>
          <DownloadIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Export Data
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 , color: '#ddd'}}>
            Export all customers, staff, and suppliers with their current dues and credits
          </Typography>
        </Paper>

        {/* Status Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Export Button */}
        <Paper elevation={3} sx={{ p: 4, color: '#ddd' }}>
          <Typography variant="h6" gutterBottom>
            Select Date Range
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Date Range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="thisYear">This Year</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </TextField>
            </Grid>
            
            {dateRange === 'custom' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          
          <Typography variant="body2" sx={{ mb: 3 }}>
            Export business report including:<br/>
            • All customers with current balances<br/>
            • Staff members with salary information<br/>
            • Suppliers with payment status<br/>
            • Summary of total dues and credits
          </Typography>
          
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={loading}
            sx={{ 
              minWidth: 200,
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: '#bc985e',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              '&:hover': {
                bgcolor: '#2c2c2c'
              }
            }}
          >
            {loading ? 'Exporting...' : 'Export'}
          </Button>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default Export;
